/* eslint-disable no-underscore-dangle */
import BaseComponent from 'scripts/BaseComponent.js';
import axios from 'axios';
import Cart from '../../cart/cart';
import ProductCustomizerFormRoseCounts from './product-customizer-form-rose-counts';
import CustomizerProductLookup from './product-customizer-form-products';

export default class ProductCustomizerForm extends BaseComponent {
  constructor(component) {
    super(component);
    this.debug = false;

    this.refs = {
      productJson: '.js-productJson',
      productForm: '.js-productCustomizerForm',
      cardStyleInput: '.js-cardStyleInput',
      cardMessageInput: '.js-cardMessageInput',
      cardMessageArea: '.js-cardMessageTextarea',
      customizerWrap: '.js-formWrap',
      customizerForm: '#tk-treble-form',
      propertyInput: '.js-lineItemProperty',
    };

    this.dom = {
      $component: component,
      $productJson: document.querySelector(this.refs.productJson),
      $productForm: this.$get(this.refs.productForm),
      $cardStyleInput: document.querySelector(this.refs.cardStyleInput),
      $cardMessageInput: document.querySelector(this.refs.cardStyleInput),
      $cardMessageArea: this.$get(this.refs.cardMessageArea),
      $customizerWrap: this.$get(this.refs.customizerWrap),
      $customizerForm: this.$get(this.refs.customizerForm),
      $propertyInputs: this.$getAll(this.refs.propertyInput),
    };

    this.state = {
      productData: {},
      productId: '',
      productBoxAttribute: '',
      productSymbolAttributes: [],
      productSnapshot: '',
      selectedConfig: {},
      cameraPosition: [],
      quickAddEvent: 'quickadd',
      shimmerClass: 'LoadingShimmer',
    };

    this.mount();
    this.init();
  }

  mount() {
    const { dom, state } = this;
    super.mount();

    this.setOnSubmit(dom.$productForm, this.formSubmit);
    this.setOnCustomEvent(state.quickAddEvent, this.formSubmit);
  }

  init() {
    const { dom } = this;
    this.parseJson();
    this.lookupProductDetails();

    const config = { childList: true };
    const observer = new MutationObserver(this.formLoaded.bind(this));
    observer.observe(dom.$customizerForm, config);
  }

  parseJson() {
    const { dom, state } = this;
    state.productData = JSON.parse(dom.$productJson.innerHTML);
  }

  lookupProductDetails() {
    const { state } = this;
    state.productId = state.productData.handle;
    if (CustomizerProductLookup[state.productId]) {
      state.productBoxAttribute = CustomizerProductLookup[state.productId].boxAttribute;
      state.productSymbolAttributes = CustomizerProductLookup[state.productId].attributes;
    }
  }

  formLoaded() {
    const { dom, state } = this;
    dom.$customizerWrap.classList.remove(state.shimmerClass);
    const pos = window.threekit.player.camera.getPosition();
    const quat = window.threekit.player.camera.getQuaternion();
    const { renderType } = CustomizerProductLookup[state.productId];

    if (renderType !== 'vray') {
      pos.x = -0.31103685498237615;
      pos.y = 0.24828296899795532;
      pos.z = 0.6414642333984378;

      quat._x = -0.0669570571716492;
      quat._y = -0.22330057870441614;
      quat._z = -0.015377101056174803;
      quat._w = 0.9723256392836166;
    }

    window.threekit.player.camera.setPosition(pos);
    window.threekit.player.camera.setQuaternion(quat);

    this.state.cameraPosition = [
      pos,
      quat,
    ];
  }

  async getSnapshot() {
    const { state } = this;
    const [pos, quat] = state.cameraPosition;
    window.threekit.player.camera.setPosition(pos);
    window.threekit.player.camera.setQuaternion(quat);
    const [snapshot] = await window.threekit.treble.takeSnapshots(undefined, { output: 'url', size: { width: 400, height: 400 } });
    return snapshot;
  }

  getAttrMetadata(attribute, metadata) {
    let meta = false;
    if (attribute.metadata) {
      meta = attribute.metadata[metadata];
    }
    return meta;
  }

  getSelectedAttr(attrName) {
    const { state } = this;
    let selected = {};
    const attribute = state.selectedConfig.find((attr) => attr.name === attrName);
    if (attribute) {
      selected = attribute.values.find((option) => option.assetId === attribute.value.assetId);
    }
    return selected;
  }

  getRoseCount(selection, property) {
    const { state } = this;
    const roseCounts = ProductCustomizerFormRoseCounts[state.productId];
    if (!selection) {
      return roseCounts[property];
    } if (roseCounts[selection] && roseCounts[selection][property]) {
      return roseCounts[selection][property];
    }
    return 0;
  }

  async getPictureAsset() {
    const { state } = this;

    const pictureAsset = state.selectedConfig.find((attr) => attr.name === 'Picture_Set_Frame').value.assetId;
    if (!pictureAsset) return null;

    const options = {
      method: 'GET',
      url: `https://admin-fts.threekit.com/api/assets/${pictureAsset}?bearer_token=adababce-e24d-467b-a8cc-241905e32aeb&orgId=0955c88c-1bff-48eb-ae5a-62b0ab9f1e27`,
    };

    try {
      const response = await axios.request(options);
      const contentURL = `https://admin-fts.threekit.com/api/files/${response.data.importedFileId}/content`;
      return contentURL;
    } catch (e) {
      console.error('error', e);
      return null;
    }
  }

  // Returns all product selections in object
  //  boxMaterial: selected box material name
  //  foreground: selected rose color object
  //  background: selected rose color object
  //  roseType: 'Premium Roses' OR 'Standard Roses'
  //  design: selected design in comma separated list
  //  designType: 'No Design' OR 'Custom'
  getProductSelections() {
    const { state } = this;
    const { attributes } = CustomizerProductLookup[state.productId];
    const { length } = attributes;
    const patterns = ['checkerd', 'h-stripes', 'v-stripes'];
    let selections = [];
    let noDesigns = 0;
    const options = {
      boxMaterial: this.getSelectedAttr(state.productBoxAttribute).name,
      background: this.getSelectedAttr('materialRosesFrame'),
      foreground: this.getSelectedAttr('materialRosesSymbol'),
      roseType: null,
      designType: null,
    };

    if (options.foreground.name) {
      options.roseType = this.getAttrMetadata(options.foreground, 'rose_upsell') > 0 || this.getAttrMetadata(options.background, 'rose_upsell') > 0 ? 'Premium Roses' : 'Standard Roses';

      selections = attributes.map((attr) => {
        const select = this.getSelectedAttr(attr);
        let result = '';
        if (select.name.toLowerCase() === 'solid') {
          result = 'None';
          noDesigns += 1;
        } else if (select.name.length > 1) result = select.name;
        else result = select.name.toUpperCase();

        return result;
      });

      const [first] = selections;
      if (noDesigns === length || options.foreground.name === options.background.name) {
        options.design = 'No Design';
      } else if (patterns.includes(first.toLowerCase())) {
        options.design = first;
      } else {
        options.design = selections.join(',');
      }

      options.designType = options.design === 'No Design' ? 'No Design' : 'Custom';
    }

    if (state.productId === 'mini-fleur-frame-custom') {
      const miniFrameBG = state.selectedConfig.find((attr) => attr.name === 'Material Roses');
      options.background = miniFrameBG.value;
    }

    return options;
  }

  // Handle variant info for frame products
  getFrameOptions(selections, productId) {
    const options = [];

    switch (productId) {
      case 'fleur-frame-custom':
        options.push('Acrylic');
        options.push(selections.background.name);
        break;
      case 'sunstone-garden-fleur-frame-custom':
        options.push('Acrylic');
        options.push('Sunstone Garden');
        break;
      case 'jardin-amour-fleur-frame-custom':
        options.push('Acrylic');
        options.push("Jardin D'Amour");
        break;
      case 'mini-fleur-frame-custom':
        options.push('Acrylic');
        options.push(selections.background);
        break;
      case 'small-square-white-classic-photo-box': {
        // This product uses only the color name as variant title (no 'Acrylic' prefix)
        const colorName = selections.background.name || selections.background;
        options.push(colorName);
        break;
      }
      default:
        return [];
    }

    return options;
  }

  // Takes selections object as a parameter and returns the
  // corresponding variant
  getSelectedVariant(selections) {
    const { state } = this;
    let options = [];
    options.push(selections.boxMaterial);
    options.push(selections.roseType);
    options.push(selections.designType);
    if (CustomizerProductLookup[state.productId].productType === 'frame') {
      options = this.getFrameOptions(selections, state.productId);
    }
    const selectedTitle = options.filter(Boolean).join(' / ');
    return state.productData.variants.find((variant) => variant.title === selectedTitle);
  }

  // Returns properties object containing all fulfillment information
  //  color_type: string containing all selections
  //  featured_image: snapshot of configuration for use in cart
  //  color_1: background color name
  //  _flower_1_sku: background color sku
  //  _flower_1_count: background rose count
  //  color_2: foreground color name
  //  _flower_2_sku: foreground color sku
  //  _flower_2_count: foreground rose count
  //  _petit_flower_1_count: background petit rose count
  //  _petit_flower_1_sku: background petit color sku
  //  _petit_flower_2_count: foreground petit rose count
  //  _petit_flower_2_sku: foreground petit color sku
  //  greeting_card_style: title of greeting card style
  //  greeting_card_message: greeting card message
  // _upload: image upload for picture frame
  // custom_image: boolean if there is an upload
  async getProperties(selections) {
    const { state, dom } = this;
    const properties = {
      _flower_1_count: 0,
      _flower_2_count: 0,
      _petit_flower_1_count: 0,
      _petit_flower_2_count: 0,
    };
    const { attributes } = CustomizerProductLookup[state.productId];
    const cardStyle = dom.$cardStyleInput.value;
    properties._thumbnail = await this.getSnapshot();

    dom.$propertyInputs.forEach((input) => {
      if (input.value) properties[input.name] = input.value;
    });

    if (cardStyle !== '') {
      properties.greeting_card_style = cardStyle;
      properties.greeting_card_message = dom.$cardMessageArea.value;
    }

    if (CustomizerProductLookup[state.productId].productType === 'frame') {
      const uploadAsset = await this.getPictureAsset();
      if (uploadAsset) {
        properties._upload = uploadAsset;
        properties.custom_image = true;
      }
    }

    if (!selections.designType) {
      return properties;
    }

    properties.color_type = selections.design;
    properties.color_1 = selections.background.name;
    properties._flower_1_sku = this.getAttrMetadata(selections.background, 'material_sku');

    if (selections.designType === 'Custom') {
      properties.color_2 = selections.foreground.name;
      properties._flower_2_sku = this.getAttrMetadata(selections.foreground, 'material_sku');
    }

    // parse rose counts and additional petit sku information from threekit metadata
    attributes.map((attribute) => {
      const select = this.getSelectedAttr(attribute).name.toLowerCase();

      if (selections.designType === 'Custom') {
        properties._flower_1_count += this.getRoseCount(select, '_flower_1_count');
        properties._flower_2_count += this.getRoseCount(select, '_flower_2_count');
        properties._petit_flower_1_count += this.getRoseCount(select, '_petit_flower_1_count');
        properties._petit_flower_2_count += this.getRoseCount(select, '_petit_flower_2_count');
      } else {
        properties._flower_1_count += this.getRoseCount('solid', '_flower_1_count');
      }

      return select;
    });

    if (properties._petit_flower_1_count > 0) {
      properties._petit_flower_1_sku = this.getAttrMetadata(selections.background, 'petit_material_sku');
    }

    if (properties._petit_flower_2_count > 0) {
      properties._petit_flower_2_sku = this.getAttrMetadata(selections.foreground, 'petit_material_sku');
    }

    return properties;
  }

  async formSubmit() {
    const { state } = this;
    state.selectedConfig = window.threekit.configurator.getDisplayAttributes();
    const selections = this.getProductSelections();
    const selectedVariant = this.getSelectedVariant(selections);
    const properties = await this.getProperties(selections);

    Cart.addItem(selectedVariant.id, properties);
  }
}
